/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefCallback, RefObject } from 'preact';
import { forwardRef } from 'preact/compat';
import { isEmpty } from 'ramda';
import { assignInlineVars } from '@vanilla-extract/dynamic';

import { borderInterpolations } from '../utils/UNSAFE_interpolations/borders';
import type { BorderProps } from '../utils/UNSAFE_interpolations/borders';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { colorInterpolations } from '../utils/UNSAFE_interpolations/colors';
import type { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { paddingInterpolations } from '../utils/UNSAFE_interpolations/padding';
import type { PaddingProps } from '../utils/UNSAFE_interpolations/padding';
import { ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import type { AriaProps } from '../utils/UNSAFE_interpolations/aria';
import { themeInterpolations } from '../utils/UNSAFE_interpolations/theme';
import type { ThemeProps } from '../utils/UNSAFE_interpolations/theme';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import type { Role } from '../utils/UNSAFE_roles';
import { EnvironmentProvider } from '..//UNSAFE_Environment';
import type { ThemeModifiers } from '../UNSAFE_Theme';
import { globalVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { useDensity } from '#hooks/UNSAFE_useDensity';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';

const elementTypes = ['div', 'main', 'article', 'section', 'aside'] as const;

type ElementType = (typeof elementTypes)[number];

type StyleProps = BorderProps &
  DimensionProps &
  FlexitemProps &
  ColorProps &
  PaddingProps &
  AriaProps &
  ThemeProps;

type Props = StyleProps &
  TestIdProps & {
    as?: ElementType;
    role?: Role;
    children?: ComponentChildren;
  };

const interpolations = [
  ...Object.values(borderInterpolations),
  ...Object.values(dimensionInterpolations),
  ...Object.values(flexitemInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(paddingInterpolations),
  ...Object.values(ariaInterpolations),
  ...Object.values(themeInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

type RefType = (RefObject<HTMLDivElement> | RefCallback<HTMLDivElement>) &
  (RefObject<HTMLElement> | RefCallback<HTMLElement>);

const View = forwardRef<RefType, Props>(
  (
    {
      as,
      children,
      colorScheme,
      color,
      backgroundColor,
      scale,
      density: propDensity,
      testId,
      ...rest
    },
    ref
  ) => {
    /**
     * JET-66845 Because density unit CSS vars need to be recalculated each time the scale changes, scale
     * classes can not be a child of density classes. However, we can have the scale and density classes as siblings.
     * If the scale prop is supplied, we'll set the density class as well. If density is not set in the props,
     * we will determine it via the Environment.
     */
    const themeDensity = useDensity();
    const density = propDensity ?? (scale ? themeDensity : undefined);
    const testIdProps = useTestId(testId);

    const {
      class: className,
      ariaLabelledBy,
      ariaLabel,
      ...styles
    } = mergedInterpolations({
      colorScheme,
      scale,
      density,
      backgroundColor,
      color,
      ...rest
    });
    // We need to update the environment with any theme related props specified
    const env: Partial<ThemeModifiers> = {
      ...(colorScheme && { colorScheme }),
      ...(scale && { scale }),
      ...(density && { density }),
      ...(backgroundColor && { currentBgColor: backgroundColor })
    };
    const props = {
      ...testIdProps,
      ['aria-label']: ariaLabel,
      ['aria-labelledby']: ariaLabelledBy,
      children,
      className,
      ref: ref as RefType,
      style: {
        ...styles,
        ...(backgroundColor &&
          assignInlineVars({
            [globalVars.currentBackgroundColor]: styles['backgroundColor']
          })),
        // we hardcode box-sizing: border-box on all of our Preact components.
        boxSizing: 'border-box'
      }
    };

    const Component = as || 'div';
    return isEmpty(env) ? (
      <Component {...props} />
    ) : (
      <EnvironmentProvider environment={env}>
        <Component {...props} />
      </EnvironmentProvider>
    );
  }
);

export { elementTypes, View };
